import { ChatMessageContent, ChatMessageData, ImageChatContent } from "@/services/promptEditor.service";
import { ChatImage } from "./ChatImage";
import Button from "@components/library/Button";
import { PlusIcon, UploadIcon } from "@heroicons/react/outline";
import { getMessageImageContent } from "@/lib/messages";
import { useCallback, useState } from "react";
import { UploadImageDialog } from "./UploadImageDialog";

interface Props {
  message: ChatMessageData;
  setContent?: (content: ChatMessageData["content"]) => void;
}

export const ChatImages = ({ message, setContent }: Props) => {
  const messageContent = message.content;
  const messageImageContentItems = getMessageImageContent(messageContent);

  const [uploadImageDialogOpen, setUploadImageDialogOpen] = useState(false);
  const closeUploadImageDialog = useCallback(() => setUploadImageDialogOpen(false), []);
  const onDrop = useCallback(
    (dataUrls: string[]) => {
      if (setContent) {
        if (!Array.isArray(messageContent)) {
          // This check is mainly to satisfy TypeScript.
          // If the message content is not an array, then it is not a message that can have images
          // and this component should not render content (making this callback inaccessible)
          return;
        }

        // If last image message is empty, remove it
        // (So that if the user uploads an image but has a single empty image message, it will be removed)
        // TODO: Consider removing all empty image messages instead of just the last one.
        let messageContentToSet = messageContent;
        const imageMessages: ImageChatContent[] = getMessageImageContent(messageContent);
        if (imageMessages.length > 0) {
          const lastImageMessage = imageMessages[imageMessages.length - 1];
          if (lastImageMessage.image_url.url === "") {
            messageContentToSet = messageContent.filter((item) => item !== lastImageMessage);
          }
        }

        setContent([
          ...messageContentToSet,
          ...dataUrls.map(
            (url) => ({ type: "image_url", image_url: { url, detail: "auto" } }) satisfies ChatMessageContent,
          ),
        ]);
        closeUploadImageDialog();
      }
    },
    [closeUploadImageDialog, messageContent, setContent],
  );

  if (messageImageContentItems.length === 0) {
    return null;
  }
  if (!Array.isArray(messageContent)) {
    // This check is mainly to satisfy TypeScript.
    // If the message content is not an array, then it is not a message that can have images
    // and the above `messageImageContentItems.length === 0` check should have returned.
    return null;
  }
  return (
    <div className="mt-24 flex flex-col gap-24">
      <div className="flex flex-col gap-16">
        {messageImageContentItems.map((contentItem, i) => {
          // Construct key by using the URL to avoid re-renders. If the key appears multiple times, append a number to the end.
          let key = contentItem.image_url.url;
          const keyCount = messageImageContentItems.filter((item) => item.image_url.url === key).length;
          if (keyCount > 1) {
            key += `-${i}`;
          }
          return (
            <ChatImage
              key={key}
              content={contentItem}
              onChange={(imageContentItem) => {
                if (setContent) {
                  const indexInContent = messageContent.findIndex((item) => contentItem === item);
                  setContent(
                    messageContent.map((item, index) => {
                      if (index === indexInContent) {
                        return imageContentItem;
                      } else {
                        return item;
                      }
                    }),
                  );
                }
              }}
              onDelete={() => {
                if (setContent) {
                  const indexInContent = messageContent.findIndex((item) => contentItem === item);
                  setContent(messageContent.filter((_, index) => index !== indexInContent));
                }
              }}
            />
          );
        })}
      </div>
      <div className="flex gap-12">
        <Button
          IconLeft={PlusIcon}
          size={24}
          onClick={() => {
            if (setContent) {
              setContent([...messageContent, { type: "image_url", image_url: { url: "", detail: "auto" } }]);
            }
          }}
        >
          Image
        </Button>
        <Button IconLeft={UploadIcon} size={24} onClick={() => setUploadImageDialogOpen(true)}>
          Upload image
        </Button>
        <UploadImageDialog open={uploadImageDialogOpen} onClose={closeUploadImageDialog} onDrop={onDrop} />
      </div>
    </div>
  );
};
